import { SprinklesElement } from "sprinkles-js";

/**
 * Range slider element for selecting single or min/max values
 *
 * This element handles both single and double range sliders
 * and updates the display values as the user interacts with the sliders
 */
export default class RangeSliderElement extends SprinklesElement {
  static tagName = "range-slider";
  static refs = [
    "minThumb",
    "maxThumb",
    "singleThumb",
    "minValue",
    "maxValue",
    "singleValue",
    "fill",
  ];
  static events = {
    input: [
      { method: "handleMinChange", ref: "minThumb" },
      { method: "handleMaxChange", ref: "maxThumb" },
      { method: "handleSingleChange", ref: "singleThumb" },
    ],
  };

  afterConnected() {
    this.min = parseInt(this.dataset.min);
    this.max = parseInt(this.dataset.max);
    this.isDoubleRange = this.dataset.doubleRange === "true";

    this.updateDisplay();
    this.updateFill();
  }

  handleMinChange() {
    this.#enforceMinConstraints();
    this.updateValues();
  }

  handleMaxChange() {
    this.#enforceMaxConstraints();
    this.updateValues();
  }

  #enforceMinConstraints() {
    const minVal = parseInt(this.refs.minThumb.value);
    const maxVal = parseInt(this.refs.maxThumb.value);

    if (minVal >= maxVal) {
      this.refs.minThumb.value = maxVal;
    }
  }

  #enforceMaxConstraints() {
    const minVal = parseInt(this.refs.minThumb.value);
    const maxVal = parseInt(this.refs.maxThumb.value);

    if (maxVal <= minVal) {
      this.refs.maxThumb.value = minVal;
    }
  }

  handleSingleChange() {
    this.updateValues();
  }

  updateValues() {
    this.updateDisplay();
    this.updateFill();
  }

  updateDisplay() {
    if (this.isDoubleRange) {
      this.#updateDisplayValue(this.refs.minValue, this.refs.minThumb.value);
      this.#updateDisplayValue(this.refs.maxValue, this.refs.maxThumb.value);
    } else {
      this.#updateDisplayValue(
        this.refs.singleValue,
        this.refs.singleThumb.value,
      );
    }
  }

  updateFill() {
    if (!this.refs.fill) return;

    const { left, width } = this.isDoubleRange
      ? this.#calculateDoubleRangeFill()
      : this.#calculateSingleRangeFill();

    this.refs.fill.style.left = left;
    this.refs.fill.style.width = width;
  }

  #updateDisplayValue(element, value) {
    if (element) element.textContent = value;
  }

  #calculateDoubleRangeFill() {
    const minVal = parseInt(this.refs.minThumb.value);
    const maxVal = parseInt(this.refs.maxThumb.value);
    const range = this.max - this.min;

    const minPercent = ((minVal - this.min) / range) * 100;
    const maxPercent = ((maxVal - this.min) / range) * 100;

    return {
      left: `${minPercent}%`,
      width: `${maxPercent - minPercent}%`,
    };
  }

  #calculateSingleRangeFill() {
    const singleVal = parseInt(this.refs.singleThumb.value);
    const range = this.max - this.min;
    const singlePercent = ((singleVal - this.min) / range) * 100;

    return {
      left: "0%",
      width: `${singlePercent}%`,
    };
  }
}
